const projectSettingsPath = '/project-settings';
const workspaceProjectSettingsPath = '/workspaces';
export const missingProjectRedirectPath = '/execution-runs';
export const missingProjectRedirectMessage = 'Redirecting to execution runs...';
export const projectCsvMappingAnchorId = 'xlsx-csv-mapping';
export const projectExecutionRunThresholdsAnchorId = 'execution-run-metric-thresholds';
export const projectMetricThresholdAnchorBaseIds = {
  executionRun: projectExecutionRunThresholdsAnchorId,
  suite: 'suite-metric-thresholds',
  test: 'test-metric-thresholds',
  pipeline: 'pipeline-metric-thresholds',
  stage: 'stage-metric-thresholds',
  job: 'job-metric-thresholds',
} as const;
export type ProjectMetricThresholdSettingsScope = keyof typeof projectMetricThresholdAnchorBaseIds;
const metricThresholdAnchorSuffixes = {
  passRate: 'success-rate',
  frequency: 'frequency',
  avgDuration: 'average-duration',
  flakeScore: 'flake-score',
} as const;
export type ProjectMetricThresholdMetricKey = keyof typeof metricThresholdAnchorSuffixes;
export const buildProjectMetricThresholdMetricAnchorIds = (
  scope: ProjectMetricThresholdSettingsScope,
): Record<ProjectMetricThresholdMetricKey, string> => {
  const baseId = projectMetricThresholdAnchorBaseIds[scope];
  return {
    passRate: `${baseId}-${metricThresholdAnchorSuffixes.passRate}`,
    frequency: `${baseId}-${metricThresholdAnchorSuffixes.frequency}`,
    avgDuration: `${baseId}-${metricThresholdAnchorSuffixes.avgDuration}`,
    flakeScore: `${baseId}-${metricThresholdAnchorSuffixes.flakeScore}`,
  };
};
export const projectExecutionRunThresholdMetricAnchorIds =
  buildProjectMetricThresholdMetricAnchorIds('executionRun');
const csvMappingHash = `#${projectCsvMappingAnchorId}`;
const projectIdParam = 'projectId';
const testThresholdScopes = ['executionRun', 'suite', 'test'] as const;
const operationsThresholdScopes = ['pipeline', 'stage', 'job'] as const;

export const buildProjectSettingsHref = (projectId: string): string => {
  const searchParams = new URLSearchParams({ [projectIdParam]: projectId });
  return `${projectSettingsPath}?${searchParams.toString()}`;
};

export const buildWorkspaceProjectSettingsHref = ({
  workspaceSlug,
  projectSlug,
}: {
  workspaceSlug: string;
  projectSlug: string;
}): string =>
  `${workspaceProjectSettingsPath}/${encodeURIComponent(workspaceSlug)}/projects/${encodeURIComponent(
    projectSlug,
  )}/settings`;

export const buildProjectSettingsHrefForProject = ({
  projectId,
  projectSlug,
  workspaceSlug,
}: {
  projectId: string;
  projectSlug: string;
  workspaceSlug?: string | null;
}): string =>
  workspaceSlug
    ? buildWorkspaceProjectSettingsHref({ workspaceSlug, projectSlug })
    : buildProjectSettingsHref(projectId);

export const buildProjectCsvMappingSettingsHref = (projectId: string): string => {
  return `${buildProjectSettingsHref(projectId)}${csvMappingHash}`;
};

export const buildProjectCsvMappingSettingsHrefForProject = (input: {
  projectId: string;
  projectSlug: string;
  workspaceSlug?: string | null;
}): string => `${buildProjectSettingsHrefForProject(input)}${csvMappingHash}`;

export type ProjectExecutionRunThresholdMetricKey =
  keyof typeof projectExecutionRunThresholdMetricAnchorIds;

export const buildProjectMetricThresholdsSettingsHref = (
  projectId: string,
  scope: ProjectMetricThresholdSettingsScope,
  metricKey?: ProjectMetricThresholdMetricKey,
): string => {
  const baseId = projectMetricThresholdAnchorBaseIds[scope];
  const metricAnchorIds = buildProjectMetricThresholdMetricAnchorIds(scope);
  const hash = metricKey ? `#${metricAnchorIds[metricKey]}` : `#${baseId}`;
  return `${buildProjectSettingsHref(projectId)}${hash}`;
};

export const buildProjectExecutionRunThresholdsSettingsHref = (
  projectId: string,
  metricKey?: ProjectExecutionRunThresholdMetricKey,
): string => buildProjectMetricThresholdsSettingsHref(projectId, 'executionRun', metricKey);

export const buildProjectMetricThresholdMetricSettingsHref = (
  href: string,
  scope: ProjectMetricThresholdSettingsScope,
  metricKey?: ProjectMetricThresholdMetricKey,
): string => {
  if (!metricKey) return href;
  const hash = `#${buildProjectMetricThresholdMetricAnchorIds(scope)[metricKey]}`;
  const hashIndex = href.indexOf('#');
  return hashIndex === -1 ? `${href}${hash}` : `${href.slice(0, hashIndex)}${hash}`;
};

export const buildProjectExecutionRunThresholdMetricSettingsHref = (
  href: string,
  metricKey?: ProjectExecutionRunThresholdMetricKey,
): string => buildProjectMetricThresholdMetricSettingsHref(href, 'executionRun', metricKey);

export const resolveProjectSettingsProjectId = (search: string): string | null => {
  const searchParams = new URLSearchParams(search);
  const projectId = searchParams.get(projectIdParam)?.trim() ?? '';
  return projectId.length > 0 ? projectId : null;
};

export const resolveProjectSettingsTabFromHash = (hash: string): 'test' | 'operations' | null => {
  if (hash === `#${projectCsvMappingAnchorId}`) {
    return 'test';
  }
  const testThresholdAnchorIds = testThresholdScopes.flatMap((scope) => [
    projectMetricThresholdAnchorBaseIds[scope],
    ...Object.values(buildProjectMetricThresholdMetricAnchorIds(scope)),
  ]);
  if (testThresholdAnchorIds.some((anchorId) => hash === `#${anchorId}`)) {
    return 'test';
  }
  const operationsThresholdAnchorIds = operationsThresholdScopes.flatMap((scope) => [
    projectMetricThresholdAnchorBaseIds[scope],
    ...Object.values(buildProjectMetricThresholdMetricAnchorIds(scope)),
  ]);
  if (operationsThresholdAnchorIds.some((anchorId) => hash === `#${anchorId}`)) {
    return 'operations';
  }
  return null;
};
